import { Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useGetCampaignFieldsQuery } from "../../../../../api";
import { DropdownNestedItem } from "../../../../../types/genereal";
import { CustomButton } from "../../../../ui/CustomButton";
import { NestedDropdown } from "./NestedDropdown";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { CustomToast } from "../../../../ui/CustomToast";
import { toast } from "react-toastify";
import {
  AccountSize,
  CampaignSavedData,
  DropdownItem,
  TargetIndustry,
  TargetRegion,
} from "../../../../../types/apiTypes";
import {
  toDropdownIndustries,
  toDropdownRegions,
} from "../../../../../utils/convertToDropdownNesteItem";
import { SizesDropdown } from "./SizesDropdown";
import { useGenerateJobTitles } from "../../../../../hooks";
import { AiAssistantBlock } from "../../AIAssistantBlock";
import { useAnalyzeGeoTargetingMutation } from "../../../../../api";
import { LabelWrapper } from "./LabelWrapper";
import { InputDropdown } from "./InputDropdown";
import { equals } from "../../../../../utils/equals";

type Props = {
  setResultData: React.Dispatch<React.SetStateAction<CampaignSavedData>>;
  initData: CampaignSavedData;
  prevData: CampaignSavedData;
  nextStepHandler: () => void;
  backHandler: () => void;
};

export const AddPreferencesStep = ({
  backHandler,
  nextStepHandler,
  setResultData,
  initData,
  prevData,
}: Props) => {
  const { data, isError, error } = useGetCampaignFieldsQuery();
  const [analyzeGeoTargeting, { isLoading }] = useAnalyzeGeoTargetingMutation();

  const {
    target_industries = [],
    target_regions = [],
    target_account_sizes = [],
    target_revenue = [],
  } = initData;

  const initialIndustries = toDropdownIndustries(target_industries);
  const [selectedIndustries, setSelectedIndustries] =
    useState<DropdownNestedItem[]>(initialIndustries);

  const initialRegions = toDropdownRegions(target_regions);
  const [selectedRegions, setSelectedRegions] =
    useState<DropdownNestedItem[]>(initialRegions);

  const [selectedSizes, setSelectedSizes] =
    useState<AccountSize[]>(target_account_sizes);
  const [selectedRevenues, setSelectedRevenues] =
    useState<DropdownItem[]>(target_revenue);

  useEffect(() => {
    saveData();
  }, [selectedRegions, selectedIndustries, selectedSizes, selectedRevenues]);

  useEffect(() => {
    if (isError && error) {
      toast.error(
        <CustomToast
          type="error"
          text={
            getErrorMessage(error).globalError ||
            "Failed to fetch campaign fields"
          }
          title="Error"
        />,
      );
    }
  }, [isError]);

  const campaignFields = data || {
    account_sizes: [],
    regions: [],
    industries: [],
    revenues: [],
  };

  const preparIndustries = useMemo(
    () =>
      campaignFields.industries.map((el) => ({
        ...el,
        sub_industries: el.sub_industries.map((name) => ({
          name,
          isAdded: false,
        })),
      })),
    [data],
  );

  const preparRegions = useMemo(
    () =>
      campaignFields.regions.map((el) => ({
        ...el,
        countries: el.countries.map((name) => ({ name, isAdded: false })),
      })),
    [data],
  );

  const preparRevenues = useMemo(
    () =>
      campaignFields.revenues.map((value) => ({
        value,
        isAdded: false,
        id: value,
      })),
    [data],
  );

  const industries = toDropdownIndustries(preparIndustries);
  const regions = toDropdownRegions(preparRegions);

  const saveData = () => {
    const transformedIndustries: TargetIndustry[] = selectedIndustries.map(
      (item) => ({
        name: item.name,
        sub_industries: item.selected ? [] : item.nestedItems,
        is_all_sub_industries: item.selected,
        isAdded: item.isAdded,
      }),
    );

    const transformedRegions: TargetRegion[] = selectedRegions.map((item) => ({
      name: item.name,
      countries: item.selected ? [] : item.nestedItems,
      is_all_countries: item.selected,
      isAdded: item.isAdded,
    }));

    setResultData((prev) => ({
      ...prev,
      target_industries: transformedIndustries,
      target_regions: transformedRegions,
      target_account_sizes: selectedSizes,
      target_revenue: selectedRevenues,
    }));
  };

  const { generateJobTitles, isLoadingTitles } = useGenerateJobTitles(initData);

  const summitHandler = async () => {
    if (!equals(prevData, initData)) {
      const titles = await generateJobTitles();
      setResultData((prev) => ({
        ...prev,
        target_audience: titles,
        selectedSeniority: undefined,
      }));
    }
    nextStepHandler();
  };

  const addAssistantData = async (text: string) => {
    await analyzeGeoTargeting({ text })
      .unwrap()
      .then(({ result }) => {
        const target_regions: DropdownNestedItem[] = result.target_regions.map(
          ({ name, countries }) => ({
            name,
            nestedItems: countries.map((country) => ({
              name: country,
              isAdded: true,
              parent: name,
            })),
            selected: !countries.length,
            isAdded: true,
          }),
        );

        const target_industries: DropdownNestedItem[] =
          result.target_industries.map(({ name, sub_industries }) => ({
            name,
            nestedItems: sub_industries.map((sub) => ({
              name: sub,
              isAdded: true,
              parent: name,
            })),
            selected: !sub_industries.length,
            isAdded: true,
          }));

        const mergeData = (
          org: DropdownNestedItem[],
          added: DropdownNestedItem[],
        ) => {
          const oldUnic = org.filter(
            ({ name }) => !added.some((el) => el.name === name),
          );

          const merged = added.map((el) => {
            const inList = org.find((origin) => origin.name === el.name);
            if (inList) {
              if (inList.selected) return inList;

              const mergedSum = [...inList.nestedItems];

              el.nestedItems.forEach((nestedItem) => {
                if (
                  !inList.nestedItems.some(
                    ({ name }) => name === nestedItem.name,
                  )
                )
                  mergedSum.push(nestedItem);
              });

              return {
                name: el.name,
                selected: !el.nestedItems.length,
                isAdded: true,
                nestedItems: !el.nestedItems.length ? [] : mergedSum,
              };
            }

            return el;
          });

          return [...oldUnic, ...merged];
        };

        setSelectedIndustries((prev) => mergeData(prev, target_industries));
        setSelectedRegions((prev) => mergeData(prev, target_regions));

        setSelectedSizes((prev) => {
          const target_account_sizes: AccountSize[] = [];
          result.target_account_size.forEach((el) => {
            if (prev.some((orig) => orig.min_max === el.min_max)) return;
            target_account_sizes.push({ ...el, isAdded: true });
          });

          return [...prev, ...target_account_sizes];
        });

        setSelectedRevenues((prev) => {
          const revenues: DropdownItem[] = [];
          result.target_revenue.forEach((value) => {
            if (prev.some((orig) => orig.value === value)) return;
            revenues.push({ value, isAdded: true, id: value });
          });

          return [...prev, ...revenues];
        });
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={getErrorMessage(err).globalError || "Failed to analyzed data"}
            title="Error"
          />,
        ),
      );
  };

  return (
    <Stack direction="column" gap="24px" width="100%">
      <AiAssistantBlock
        isLoading={isLoading}
        submitHandler={addAssistantData}
        title={"Describe your targeting, let AI build it"}
        subtitle={
          "AI extracts target locations, industries, company size and revenue range."
        }
        placeholder={
          "E.g. \n" +
          "Geo: \n" +
          "US, Canada, EMEA\n" +
          "Industry: \n" +
          "Finance, Retail, Energy: Oil & Gas\n" +
          "Size: \n" +
          "1000+\n" +
          "Revenue: \n" +
          "1M+"
        }
      />
      <LabelWrapper
        title="Geo"
        label="Select the region or specific countries where your target accounts are located."
      >
        <NestedDropdown
          items={regions}
          selected={selectedRegions}
          setSelected={setSelectedRegions}
          placeholder="Search Region or Countries"
          selectAllText="Whole region"
        />
      </LabelWrapper>

      <LabelWrapper
        title="Industry"
        label="Search industries or sub-industries that match your ideal customer profile."
      >
        <NestedDropdown
          items={industries}
          selected={selectedIndustries}
          setSelected={setSelectedIndustries}
          placeholder="Search Industry or Sub-Industry"
          selectAllText="Whole industry"
        />
      </LabelWrapper>

      <LabelWrapper
        title="Size"
        label="Search the company size by employees count that best fits your target accounts."
      >
        <SizesDropdown
          items={campaignFields.account_sizes}
          selected={selectedSizes}
          setSelected={setSelectedSizes}
          placeholder="Search Company Size"
        />
      </LabelWrapper>

      <LabelWrapper
        title="Revenue"
        label="Search the company revenue range that best fits your target accounts"
      >
        <InputDropdown
          items={preparRevenues}
          selected={selectedRevenues}
          setSelected={setSelectedRevenues}
          placeholder="Search Company Revenu"
        />
      </LabelWrapper>

      <Stack direction="row" gap="8px">
        <CustomButton
          variant="contained"
          textColor="#4C545B"
          fullWidth
          type="button"
          customColor="#F1F1F1"
          onClick={backHandler}
        >
          Back
        </CustomButton>
        <CustomButton
          onClick={summitHandler}
          variant="contained"
          fullWidth
          type="button"
          customColor="#1C3C6C"
          loading={isLoadingTitles}
          disabled={
            !selectedIndustries.length ||
            !selectedRegions.length ||
            !selectedSizes.length
          }
        >
          Next Step
        </CustomButton>
      </Stack>
    </Stack>
  );
};
